import { Link } from '@workos-inc/design-system';
import escapeRegExp from 'lodash.escaperegexp';
import { methodRegExp } from './type-definition-entity';
interface TypeDefinitionTokenProps {
  token?: string;
  href?: string;
  className?: string;
}

/** Renders a type definition code fragment with syntax highlighting. */
export const TypeDefinitionToken = ({
  token = '',
  href,
  className = 'token class-name'
}: TypeDefinitionTokenProps) => {
  if (isString(token)) {
    return <span className="token string">{token}</span>;
  }
  if (isMethod(token)) {
    return <>
        {splitMethod(token).map((fragment, index) => <TypeDefinitionToken key={index} className={index === 0 ? 'token function' : 'token'} href={href} token={fragment} />)}
      </>;
  }
  if (token === ' ') {
    return <> </>;
  }
  if (token.includes(' ')) {
    return <>
        {token.split(/( )/).filter(Boolean).map((fragment, index) => <TypeDefinitionToken key={index} className={className} href={href} token={fragment} />)}
      </>;
  }
  if (hasPunctuation(token)) {
    return <>
        {splitByPunctuation(token).map((fragment, index) => <TypeDefinitionToken key={index} className={className} href={href} token={fragment} />)}
      </>;
  }
  if (hasOperator(token)) {
    return <>
        {splitByOperator(token).map((fragment, index) => <TypeDefinitionToken key={index} className={className} href={href} token={fragment} />)}
      </>;
  }
  if (isKeyword(token)) {
    className = 'token keyword';
  }
  if (isPunctuation(token)) {
    className = 'token punctuation';
  }
  if (isOperator(token)) {
    className = 'token operator';
  }
  if (className === 'token class-name' && href) {
    return <span className={className}>
        <Link href={href}>{token}</Link>
      </span>;
  }
  return <span className={className} data-sentry-component="TypeDefinitionToken" data-sentry-source-file="type-definition-token.tsx">{token}</span>;
};
const isMethod = (str: string) => methodRegExp.test(str);
const isKeyword = (str: string) => keywords.includes(str);
const isString = (str: string) => /(^"[^"]+?"$)|(^'[^']+?'$)/.test(str);
const isOperator = (str: string) => operators.includes(str);
const isPunctuation = (str: string) => punctuation.includes(str);
const hasPunctuation = (str: string) => !isPunctuation(str) && punctuation.some(symbol => str.includes(symbol));
const hasOperator = (str: string) => !isOperator(str) && operators.some(operator => str.includes(operator));
const punctuation = ['.', ',', ';', '(', ')', '\\'];
const operators = ['*', '=', ':', '?', '|', ' | ', '[', ']', '<', '>', '->', '{', '}', 'void'];
const splitByPunctuation = (str: string) => {
  const splitters = punctuation.map(escapeRegExp).join('|');
  const regexp = new RegExp(`(${splitters})`);
  return str.split(regexp).filter(Boolean);
};
const splitByOperator = (str: string) => {
  const splitters = operators.map(escapeRegExp).join('|');
  const regexp = new RegExp(`(${splitters})`);
  return str.split(regexp).filter(Boolean);
};
const splitMethod = (str: string) => {
  const [_, before, args, after] = str.match(methodRegExp) ?? [];
  // Introducing lint rule banning type assertions
  // How is this safe? before, args, after may each be undefined
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return [before, args, after].filter(Boolean) as string[];
};
const keywords = ['any', 'Any', 'array', 'Array', 'bool', 'Bool', 'boolean', 'Boolean', 'class', 'const', 'dict', 'enum', 'error', 'extends', 'Hash', 'int', 'Int', 'integer', 'Integer', 'interface', 'list', 'map', 'number', 'object', 'str', 'string', 'String', 'Struct'];